import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";
import { getBlogPost, listBlogPosts } from "@/lib/api";
import { renderMarkdown } from "@/lib/markdown";

export default function BlogPost() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [others, setOthers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
        setLoading(true);
        Promise.all([getBlogPost(slug), listBlogPosts()])
            .then(([p, all]) => {
                setPost(p);
                setOthers(all.filter((x) => x.slug !== slug).slice(0, 2));
            })
            .catch((e) => setError(e?.response?.data?.detail || e.message))
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) return <main style={{ paddingTop: 200, textAlign: "center", color: "var(--ink-soft)" }}>Chargement…</main>;
    if (error) return <main style={{ paddingTop: 200, textAlign: "center", color: "var(--destructive)" }}>{error}</main>;
    if (!post) return null;

    const bodyHtml = renderMarkdown(post.body);

    return (
        <main data-testid="blog-post-page" style={{ paddingTop: 120 }}>
            {/* Cover */}
            <section style={{ padding: "20px 40px 40px" }}>
                <div className="mx-auto" style={{ maxWidth: 1100 }}>
                    <Link
                        to="/journal"
                        data-testid="blog-post-back"
                        className="link-underline inline-flex items-center gap-2 mb-10"
                        style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-soft)" }}
                    >
                        <ArrowLeft size={14} /> Retour au journal
                    </Link>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9 }}
                    >
                        <div className="flex items-center gap-4" style={{ fontSize: 11, color: "var(--terra)", textTransform: "uppercase", letterSpacing: "0.16em", fontWeight: 500 }}>
                            <span>{post.category}</span>
                            <span style={{ color: "var(--hairline)" }}>·</span>
                            <span>{new Date(post.published_at).toLocaleDateString("fr-CH", { day: "numeric", month: "long", year: "numeric" })}</span>
                            <span style={{ color: "var(--hairline)" }}>·</span>
                            <span className="flex items-center gap-1"><Clock size={11} /> {post.reading_time} min</span>
                        </div>

                        <h1
                            data-testid="blog-post-title"
                            className="font-display mt-8"
                            style={{
                                fontSize: "clamp(2.5rem, 6vw, 5rem)",
                                lineHeight: 1.05,
                                color: "var(--forest-dark)",
                                fontWeight: 400,
                                letterSpacing: "-0.015em",
                            }}
                        >
                            {post.title}
                        </h1>

                        <p
                            className="font-display mt-8"
                            style={{
                                fontSize: "1.4rem",
                                lineHeight: 1.5,
                                color: "var(--ink-mid)",
                                fontStyle: "italic",
                                maxWidth: 780,
                            }}
                        >
                            {post.excerpt}
                        </p>
                    </motion.div>
                </div>
            </section>

            <section style={{ padding: "20px 40px 40px" }}>
                <div className="mx-auto" style={{ maxWidth: 1100 }}>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.2, delay: 0.2 }}
                        style={{ aspectRatio: "16 / 9", overflow: "hidden", borderRadius: 4 }}
                    >
                        <img src={post.cover_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </motion.div>
                </div>
            </section>

            {/* Body */}
            <section style={{ padding: "60px 40px 80px" }}>
                <div className="mx-auto grid grid-cols-1 md:grid-cols-12 gap-16" style={{ maxWidth: 1100 }}>
                    <aside className="md:col-span-3">
                        <div style={{ position: "sticky", top: 120 }}>
                            <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Auteur</div>
                            <div className="divider-terra mt-3" />
                            <div className="font-display mt-4" style={{ fontSize: 20, color: "var(--forest-dark)" }}>Ana</div>
                            <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 4 }}>Physiothérapeute diplômée</div>
                            <Link to="/rendez-vous" data-testid="blog-post-cta-appointment" className="btn-primary mt-8 inline-flex" style={{ fontSize: 12, padding: "10px 20px" }}>
                                Prendre RDV <ArrowRight size={13} />
                            </Link>
                        </div>
                    </aside>

                    <article
                        className="md:col-span-9 prose-clinical"
                        data-testid="blog-post-body"
                        dangerouslySetInnerHTML={{ __html: bodyHtml }}
                    />
                </div>
            </section>

            {/* Other posts */}
            {others.length > 0 && (
                <section data-testid="blog-related-posts" style={{ padding: "80px 40px 120px", background: "var(--cream-warm)", borderTop: "1px solid var(--hairline)" }}>
                    <div className="mx-auto" style={{ maxWidth: 1400 }}>
                        <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Autres notes cliniques</div>
                        <div className="divider-terra mt-3" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
                            {others.map((p) => (
                                <Link
                                    key={p.slug}
                                    to={`/journal/${p.slug}`}
                                    style={{ textDecoration: "none", color: "inherit" }}
                                >
                                    <div style={{ aspectRatio: "16 / 9", overflow: "hidden", borderRadius: 4, marginBottom: 20 }}>
                                        <img src={p.cover_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    </div>
                                    <div style={{ fontSize: 11, color: "var(--terra)", textTransform: "uppercase", letterSpacing: "0.15em" }}>{p.category}</div>
                                    <h3 className="font-display mt-3" style={{ fontSize: "1.6rem", color: "var(--forest-dark)", lineHeight: 1.2, fontWeight: 400 }}>{p.title}</h3>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </main>
    );
}
